import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Globe, Smartphone, Briefcase, Code2, Database, Search, Bot, Cloud, Workflow, BarChart3, Users, ArrowUpRight } from "lucide-react";
import { services as defaultServices } from "../mock";

const iconMap = { Globe, Smartphone, Briefcase, Code2, Database, Search, Bot, Cloud, Workflow, BarChart3, Users };

function ServiceCard({ s, i, Icon, isAI, isStaffing }) {
  const ref = React.useRef(null);
  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left, y = e.clientY - r.top;
    const rx = ((y / r.height) - 0.5) * -10;
    const ry = ((x / r.width) - 0.5) * 10;
    el.style.setProperty("--rx", rx + "deg");
    el.style.setProperty("--ry", ry + "deg");
    el.style.setProperty("--mx", (x / r.width * 100) + "%");
    el.style.setProperty("--my", (y / r.height * 100) + "%");
  };
  const onLeave = () => {
    const el = ref.current; if (!el) return;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
  };

  return (
    <motion.a
      href="#contact"
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: i * 0.07, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="group relative rounded-2xl bg-white border border-[#E5E5E5] p-7 block hover:border-[#73100D]/30 transition-all duration-500 hover:shadow-[0_18px_50px_-18px_rgba(115,16,13,0.18)] overflow-hidden"
      style={{ transform: "perspective(1000px) rotateX(var(--rx,0)) rotateY(var(--ry,0))", transition: "transform 0.25s ease-out, border-color 0.4s, box-shadow 0.4s" }}
    >
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: "radial-gradient(280px circle at var(--mx,50%) var(--my,50%), rgba(115,16,13,0.10), transparent 60%)" }}
      />
      {isAI && (
        <div className="absolute top-5 right-5 text-[9px] font-bold tracking-[0.2em] uppercase text-white bg-[#73100D] px-2 py-0.5 rounded font-mono">
          AI
        </div>
      )}
      {isStaffing && (
        <div className="absolute top-5 right-5 text-[9px] font-bold tracking-[0.2em] uppercase text-white bg-[#73100D] px-2 py-0.5 rounded font-mono">
          C2H
        </div>
      )}
      <div className="relative">
        {/* Floating + ring-spinning icon */}
        <motion.div
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", delay: i * 0.25 }}
          className="relative w-12 h-12 grid place-items-center mb-5"
        >
          <div className="absolute -inset-1 rounded-xl icon-ring opacity-35 spin-slow" />
          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#73100D] to-[#5E0000]" />
          <Icon className="relative w-5 h-5 text-white" />
        </motion.div>
        <h3 className="text-base font-semibold text-[#111] group-hover:text-[#73100D] transition-colors">{s.title}</h3>
        <p className="mt-2 text-sm text-[#555] leading-relaxed">{s.desc}</p>
        <div className="mt-5 inline-flex items-center gap-1.5 text-[#73100D] text-sm font-semibold">
          Learn more
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </motion.a>
  );
}

export default function Services() {
  const [services, setServices] = useState(defaultServices);
  useEffect(() => {
    fetch("/api/content/services")
      .then((r) => r.ok ? r.json() : null)
      .then((json) => { if (json?.data) setServices(json.data); })
      .catch(() => {});
  }, []);

  return (
    <section id="services" className="relative py-14 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-2xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#73100D]/25 bg-[#73100D]/5 text-[#73100D] text-[11px] font-semibold tracking-[0.18em] uppercase mb-5">
            What We Build
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[#111] leading-tight">
            Engineering services for{" "}
            <span className="brand-gradient-text">AI-driven growth</span>
          </h2>
          <p className="mt-4 text-[#555]">
            End-to-end engineering, intelligent automation and digital products crafted to scale with your business.
          </p>
        </motion.div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => {
            const Icon = iconMap[s.icon] || Code2;
            const isAI = s.icon === "Bot" || s.icon === "Workflow" || s.icon === "BarChart3";
            const isStaffing = s.icon === "Users";
            return <ServiceCard key={s.id} s={s} i={i} Icon={Icon} isAI={isAI} isStaffing={isStaffing} />;
          })}
        </div>
      </div>
    </section>
  );
}
